import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  IconBuildingStore,
  IconAdjustments,
  IconSettings,
  IconClipboardCheck,
  IconLockOpen,
  IconUserCheck,
  IconTags,
  IconTruckDelivery,
} from '@tabler/icons-react';
import { Search, User, Mail, Phone, Hash, Briefcase, MapPin, X, Package, Star, UserPlus, Info, ChevronDown, KeyRound, Eye, EyeOff } from 'lucide-react';
import { fetchPassPolicy } from '../../security';
import { validatePasswordForPolicy } from '../../security/passwordPolicyValidation';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import { getInitials } from '../utils/equipe2Helpers';
import { Equipe2TechDrawerSpecialty } from './Equipe2TechDrawerSpecialty';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import './Equipe2TechDetailDrawer.css';
import './TechnicianModal.css';

function pinWidthForLen(len) {
  const n = Number(len) || 2;
  return Math.max(36, 12 + n * 8);
}

/** Kit Técnico por técnico (pins); Classificação/Transportadora ficam no mesmo card. */
const TECH_KIT_FIELDS = [
  {
    api: 'kitClientCode',
    label: 'Código de cliente',
    kind: 'num',
    len: 8,
    help: 'Override do código de cliente do Kit. Vazio = usa a configuração global.',
    section: 'ident',
    Icon: IconBuildingStore,
  },
  {
    api: 'kitConditionCode',
    label: 'Condição',
    kind: 'alnum',
    len: 3,
    help: 'Condição de recebimento no pedido do Kit. Vazio = configuração global.',
    section: 'ident',
    Icon: IconAdjustments,
  },
  {
    api: 'kitOperationCode',
    label: 'Operação',
    kind: 'alnum',
    len: 2,
    help: 'Código da operação de saída (2 dígitos). Vazio = configuração global.',
    section: 'ident',
    Icon: IconSettings,
  },
  {
    api: 'kitStPendAvail',
    label: 'Status - Kit pendente',
    stockTag: 'disponível no estoque',
    stockTone: 'ok',
    kind: 'alnum',
    len: 2,
    help: 'Status quando o kit ainda está pendente e há estoque. Vazio = global.',
    section: 'pend',
    Icon: IconClipboardCheck,
  },
  {
    api: 'kitStPendUnavail',
    label: 'Status - Kit pendente',
    stockTag: 'indisponível no estoque',
    stockTone: 'warn',
    kind: 'alnum',
    len: 2,
    help: 'Status quando o kit ainda está pendente sem estoque. Vazio = global.',
    section: 'pend',
    Icon: IconClipboardCheck,
  },
  {
    api: 'kitStReleased',
    label: 'Status - Kit liberado',
    kind: 'alnum',
    len: 2,
    help: 'Status liberado para coleta. Vazio = configuração global.',
    section: 'status',
    Icon: IconLockOpen,
  },
  {
    api: 'kitStReceivedTech',
    label: 'Status - Recebido pelo técnico',
    kind: 'alnum',
    len: 2,
    help: 'Status após coleta pelo técnico. Vazio = configuração global.',
    section: 'status',
    Icon: IconUserCheck,
  },
];

const KIT_IDENT_FIELDS = TECH_KIT_FIELDS.filter((f) => f.section === 'ident');
const KIT_PEND_FIELDS = TECH_KIT_FIELDS.filter((f) => f.section === 'pend');
const KIT_STATUS_FIELDS = TECH_KIT_FIELDS.filter((f) => f.section === 'status');
const KIT_PEND_HELP =
  'Duas configurações separadas: uma para quando o item está disponível no estoque, outra para quando está indisponível.';

const EMPTY_KIT_DRAFT = TECH_KIT_FIELDS.reduce((acc, f) => {
  acc[f.api] = '';
  return acc;
}, {});

const EMPTY_PROFILE = {
  name: '',
  document: '',
  phone: '',
  email: '',
  isTerritory: false,
  userLegacyId: '',
  erpCode: '',
  jobTitle: '',
  areaId: '',
  isActive: true,
};
const CREATE_USER_VALUE = '__new__';

const NAME_PARTICLE = new Set(['da', 'de', 'do', 'das', 'dos', 'e', 'di', 'du']);
const BR_DDD = new Set([
  '11', '12', '13', '14', '15', '16', '17', '18', '19',
  '21', '22', '24', '27', '28',
  '31', '32', '33', '34', '35', '37', '38',
  '41', '42', '43', '44', '45', '46', '47', '48', '49',
  '51', '53', '54', '55',
  '61', '62', '63', '64', '65', '66', '67', '68', '69',
  '71', '73', '74', '75', '77', '79',
  '81', '82', '83', '84', '85', '86', '87', '88', '89',
  '91', '92', '93', '94', '95', '96', '97', '98', '99',
]);

function formatCpf(raw) {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

function isValidCpf(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digit = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i += 1) sum += Number(d[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
}

function personNameError(raw) {
  const name = String(raw || '').replace(/\s+/g, ' ').trim();
  if (name.length > 60) return 'Nome deve ter no máximo 60 caracteres';
  const parts = name.split(' ').filter(Boolean);
  const letters = /^[\p{L}]+$/u;
  const significant = parts.filter((p) => letters.test(p) && !NAME_PARTICLE.has(p.toLocaleLowerCase('pt-BR')));
  const real = significant.length >= 2
    && significant.every((p) => p.length >= 2 && !/^(.)\1+$/iu.test(p))
    && parts.every((p) => letters.test(p));
  return real ? '' : 'Informe nome e sobrenome, sem números ou abreviações de uma letra';
}

function formatPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('55') && d.length > 11) d = d.slice(2);
  d = d.slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneError(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  const d = digits.startsWith('55') && (digits.length === 12 || digits.length === 13) ? digits.slice(2) : digits;
  const rest = d.slice(2);
  const ok = (d.length === 10 || d.length === 11)
    && BR_DDD.has(d.slice(0, 2))
    && !/^(\d)\1+$/.test(rest)
    && (d.length === 11 ? rest[0] === '9' : /^[2-5]/.test(rest));
  return ok ? '' : 'Informe um telefone com DDD válido do Brasil';
}

function emailError(raw) {
  const email = String(raw || '').trim();
  if (!email) return '';
  const ok = email.length <= 150
    && /^[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(email)
    && !email.includes('..');
  return ok ? '' : 'E-mail inválido';
}

function profileFieldErrors(profile, { createUser = false } = {}) {
  const errors = {};
  const nameErr = personNameError(profile.name);
  if (nameErr) errors.name = nameErr;
  if (createUser && !String(profile.email || '').trim()) errors.email = 'Informe o e-mail do usuário';
  const mailErr = emailError(profile.email);
  if (mailErr) errors.email = mailErr;
  const doc = String(profile.document || '').replace(/\D/g, '');
  if (doc && !isValidCpf(doc)) errors.document = 'CPF inválido';
  const telErr = phoneError(profile.phone);
  if (telErr) errors.phone = telErr;
  return errors;
}

function TmField({ label, htmlFor, required, hint, error, children, className = '' }) {
  return (
    <div className={`tm-field ${className}`}>
      <label className="tm-label" htmlFor={htmlFor}>
        {label}
        {required ? <span className="tm-required">*</span> : null}
      </label>
      {children}
      {error ? <p className="tm-field-error" role="alert">{error}</p> : (hint ? <p className="tm-hint">{hint}</p> : null)}
    </div>
  );
}

function TmPicker({ id, value, onChange, options, placeholder, emptyMessage, ariaLabel }) {
  const anchorRef = useRef(null);
  const popId = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [layout, setLayout] = useState(null);
  const selected = options.find((o) => String(o.value) === String(value));
  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [options, q]);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = computeToolbarFilterPopoverLayout(rect, Math.max(238, Math.round(rect.width)), 320);
    setLayout({ ...next, zIndex: 3100 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setLayout(null);
      return;
    }
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) {
      setQ('');
      return undefined;
    }
    const onDoc = (e) => {
      const pop = document.querySelector(`[data-tm-picker="${popId}"]`);
      if (pop?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, popId, place]);

  return (
    <div className={`tm-combo${open ? ' is-open' : ''}`}>
      <button
        type="button"
        id={id}
        ref={anchorRef}
        className="tm-control tm-combo-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <span className="tm-combo-value">{selected.label}</span>
        ) : (
          <span className="tm-combo-placeholder">{placeholder}</span>
        )}
        <ChevronDown className="tm-combo-chevron" size={15} strokeWidth={1.75} />
      </button>
      {open && layout ? ReactDOM.createPortal(
        <div
          data-tm-picker={popId}
          className={EC_FILTER_POPOVER_CLASS}
          style={buildEcFilterPopoverStyle(layout)}
          role="dialog"
          aria-label={ariaLabel}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <BoardSearchSimplePickerPanel
            searchPlaceholder="Buscar"
            searchTerm={q}
            onSearchChange={setQ}
            items={items}
            getKey={(o) => String(o.value)}
            getLabel={(o) => o.label}
            selectedKeys={[String(value ?? '')]}
            onToggle={(o) => {
              onChange(String(o.value) === String(value) ? '' : String(o.value));
              setOpen(false);
            }}
            emptyMessage={emptyMessage}
            listAriaLabel={ariaLabel}
          />
        </div>,
        document.body
      ) : null}
    </div>
  );
}

function TmText({ icon: Icon, className = '', ...props }) {
  return (
    <div className={`tm-input-wrap${Icon ? ' has-icon' : ''}`}>
      {Icon ? <Icon className="tm-input-icon" size={15} strokeWidth={1.75} /> : null}
      <input className={`tm-control${className ? ` ${className}` : ''}`} {...props} />
    </div>
  );
}

function TmSwitch({ checked, onChange, id }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      className={`tm-switch${checked ? ' is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="tm-switch-thumb" />
    </button>
  );
}

function sanitizeKitDraftInput(raw, kind, len) {
  const s = String(raw ?? '')
    .toUpperCase()
    .replace(kind === 'num' ? /[^0-9]/g : /[^0-9A-Z]/g, '');
  return s.slice(0, len);
}

function kitDraftFromSelected(selected) {
  if (!selected) return { ...EMPTY_KIT_DRAFT };
  const next = { ...EMPTY_KIT_DRAFT };
  TECH_KIT_FIELDS.forEach((f) => {
    next[f.api] = String(selected[f.api] || '').trim();
  });
  return next;
}

function TdpLabelWithHelp({ children, helpTooltip }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [tooltipLayout, setTooltipLayout] = useState(null);
  const helpWrapRef = useRef(null);
  const btnRef = useRef(null);
  const helpTooltipId = useId();

  const computeTooltipLayout = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const pad = 12;
    const maxW = 260;
    const gap = 8;
    const top = rect.bottom + gap;
    const btnCenter = rect.left + rect.width / 2;

    let left = btnCenter - maxW / 2;
    let align = 'center';

    if (left + maxW > window.innerWidth - pad) {
      left = rect.right - maxW;
      align = 'end';
    }
    if (left < pad) {
      left = rect.left;
      align = 'start';
    }
    left = Math.max(pad, Math.min(left, window.innerWidth - pad - maxW));

    return { top, left, align, maxWidth: maxW };
  }, []);

  useLayoutEffect(() => {
    if (!helpOpen) {
      setTooltipLayout(null);
      return undefined;
    }
    const update = () => setTooltipLayout(computeTooltipLayout());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [helpOpen, computeTooltipLayout]);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onDoc = (e) => {
      if (helpWrapRef.current?.contains(e.target)) return;
      setHelpOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [helpOpen]);

  if (!helpTooltip) {
    return children ? <label className="eq2-tdp-label">{children}</label> : null;
  }

  const ariaLabel =
    typeof children === 'string' && children
      ? `Ajuda: ${children}`
      : 'Mais informações';

  return (
    <div className={`eq2-tdp-label-row${children ? '' : ' eq2-tdp-label-row--help-only'}`}>
      {children ? <label className="eq2-tdp-label">{children}</label> : null}
      <span className="eq2-tdp-help-wrap" ref={helpWrapRef}>
        <button
          ref={btnRef}
          type="button"
          className="eq2-tdp-help-btn eq2-tdp-help-btn--dot"
          aria-label={ariaLabel}
          aria-expanded={helpOpen}
          aria-describedby={helpOpen ? helpTooltipId : undefined}
          onClick={() => setHelpOpen((o) => !o)}
        >
          ?
        </button>
        {helpOpen && tooltipLayout
          ? ReactDOM.createPortal(
              <span
                className={[
                  'eq2-tdp-help-tooltip',
                  'eq2-tdp-help-tooltip--portal',
                  `eq2-tdp-help-tooltip--${tooltipLayout.align}`,
                ].join(' ')}
                id={helpTooltipId}
                role="tooltip"
                style={{
                  position: 'fixed',
                  top: tooltipLayout.top,
                  left: tooltipLayout.left,
                  maxWidth: tooltipLayout.maxWidth,
                  zIndex: 1302,
                }}
              >
                {helpTooltip}
              </span>,
              document.body,
            )
          : null}
      </span>
    </div>
  );
}

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function TdpSearchField({
  label,
  placeholder,
  value,
  onChange,
  fetchSuggestions,
  mono = false,
  icon: Icon = Search,
}) {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value) {
      setEditing(false);
      setQuery('');
    } else {
      setEditing(true);
    }
  }, [value]);

  useEffect(() => {
    if (!editing || query.trim().length < 3) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const tid = setTimeout(() => {
      fetchSuggestions(query.trim())
        .then((list) => {
          if (!cancelled) setItems(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 260);
    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [query, editing, fetchSuggestions]);

  useEffect(() => {
    if (!editing) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (value) setEditing(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [editing, value]);

  const showList = editing && query.trim().length >= 3;

  const pickItem = (item) => {
    Promise.resolve(onChange(item))
      .then(() => {
        setQuery('');
        setEditing(false);
      })
      .catch(() => {});
  };

  const suggestBlock = showList ? (
    <>
      {loading ? <div className="eq2-tdp-suggest eq2-tdp-suggest--muted">Buscando…</div> : null}
      {!loading && items.length > 0 ? (
        <ul className="eq2-tdp-suggest-list eq2-tdp-suggest-list--kit">
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" className="eq2-tdp-suggest-item" onMouseDown={() => pickItem(item)}>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!loading && items.length === 0 ? (
        <div className="eq2-tdp-suggest eq2-tdp-suggest--muted">Nenhum resultado</div>
      ) : null}
    </>
  ) : null;

  return (
    <div className="eq2-kit-row eq2-kit-row--search" ref={wrapRef}>
      <span className="eq2-kit-row-icon" aria-hidden>
        <Icon size={14} stroke={1.75} />
      </span>
      <span className="eq2-kit-row-label eq2-kit-row-label--fixed">{label}</span>
      <div className="eq2-kit-search">
        {value && !editing ? (
          <div className="eq2-kit-search-filled">
            <span className={`eq2-kit-search-value${mono ? ' eq2-tdp-mono' : ''}`}>{value.label}</span>
            <button
              type="button"
              className="eq2-kit-search-clear"
              onClick={() => onChange(null)}
              aria-label={`Remover ${label}`}
            >
              ×
            </button>
          </div>
        ) : (
          <input
            type="text"
            className={`eq2-kit-search-input${mono ? ' eq2-tdp-mono' : ''}`}
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setEditing(true)}
            autoComplete="off"
          />
        )}
        {suggestBlock}
      </div>
    </div>
  );
}

function Eq2KitPinInput({ field, value, onChange }) {
  const filled = String(value || '').trim().length > 0;
  return (
    <input
      type="text"
      className={`eq2-kit-pin${filled ? ' is-filled' : ''} eq2-tdp-mono`}
      style={{ width: pinWidthForLen(field.len) }}
      value={value}
      onChange={(e) => onChange(sanitizeKitDraftInput(e.target.value, field.kind, field.len))}
      maxLength={field.len}
      placeholder="—"
      spellCheck={false}
      autoComplete="off"
      inputMode={field.kind === 'num' ? 'numeric' : 'text'}
      aria-label={field.stockTag ? `${field.label} (${field.stockTag})` : field.label}
    />
  );
}

export function Equipe2TechDetailDrawer({
  open,
  tech,
  baseUrl,
  areas = [],
  coordenadores = [],
  allowProfile = false,
  profileMode = null,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [gestaoList, setGestaoList] = useState([]);
  const [draftCargo, setDraftCargo] = useState('');
  const [draftKit, setDraftKit] = useState(() => ({ ...EMPTY_KIT_DRAFT }));
  const [classificacao, setClassificacao] = useState(null);
  const [transportadora, setTransportadora] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [enabling, setEnabling] = useState(false);
  const [enableErr, setEnableErr] = useState(null);
  const [activeTab, setActiveTab] = useState('geral');
  const [kitFilter, setKitFilter] = useState('all');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [profileBase, setProfileBase] = useState(EMPTY_PROFILE);
  const [profileSource, setProfileSource] = useState('native');
  const [profileUsers, setProfileUsers] = useState([]);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [passPolicy, setPassPolicy] = useState(null);
  const [passwordRules, setPasswordRules] = useState([]);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [coordId, setCoordId] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const photoInputRef = useRef(null);
  const photoBusyRef = useRef(false);
  const enableAttemptRef = useRef(null);

  const codigo = tech?.techId;
  const selected = gestaoList.find((t) => String(t.codigo) === String(codigo)) || null;

  const loadGestao = useCallback(async () => {
    if (!baseUrl) return;
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Falha ao carregar dados do técnico');
      }
      setGestaoList(json.data || []);
    } catch {
      setGestaoList([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!open || !codigo) return;
    loadGestao();
  }, [open, codigo, loadGestao]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const isCreate = allowProfile && profileMode === 'create';
  const areaChoices = useMemo(() => {
    if (!coordId) return areas;
    return areas.filter((a) => a.coordenadorId == null || String(a.coordenadorId) === String(coordId));
  }, [areas, coordId]);
  const selectedArea = areas.find((a) => String(a.id) === String(profile.areaId));
  const coordChoices = useMemo(() => {
    if (!profile.areaId || selectedArea?.coordenadorId == null) return coordenadores;
    return coordenadores.filter((c) => String(c.id) === String(selectedArea.coordenadorId));
  }, [coordenadores, profile.areaId, selectedArea]);
  const showProfile = allowProfile && (isCreate || profileMode === 'edit');

  useEffect(() => {
    setTransportadora(null);
    setClassificacao(null);
    setSaveErr(null);
    setEnableErr(null);
    setDraftKit({ ...EMPTY_KIT_DRAFT });
    setKitFilter('all');
    setActiveTab(allowProfile ? 'geral' : 'kit');
    enableAttemptRef.current = null;
  }, [codigo, open, allowProfile]);

  const enableGestao = useCallback(async () => {
    if (!baseUrl || !codigo) return;
    setEnabling(true);
    setEnableErr(null);
    try {
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/habilitacao`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, habilitado: true }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Não foi possível habilitar o técnico');
      }
      setGestaoList(json.data || []);
    } catch (e) {
      setEnableErr(e.message || 'Erro ao habilitar técnico');
      enableAttemptRef.current = null;
    } finally {
      setEnabling(false);
    }
  }, [baseUrl, codigo]);

  useEffect(() => {
    if (!open || !codigo || loading || enabling || enableErr) return;
    if (!selected || selected.gestaoHabilitado) return;
    if (enableAttemptRef.current === String(codigo)) return;
    enableAttemptRef.current = String(codigo);
    enableGestao();
  }, [open, codigo, loading, enabling, enableErr, selected, enableGestao]);

  useEffect(() => {
    if (!selected) {
      setDraftCargo('');
      setDraftKit({ ...EMPTY_KIT_DRAFT });
      return;
    }
    setDraftCargo(String(selected.cargo || ''));
    setDraftKit(kitDraftFromSelected(selected));
    if (!selected.gestaoHabilitado) {
      setClassificacao(null);
      setTransportadora(null);
      return;
    }
    if (selected.idClass && selected.classificacaoLabel) {
      setClassificacao({ id: String(selected.idClass), label: selected.classificacaoLabel });
    } else {
      setClassificacao(null);
    }
    if (selected.idTransp && selected.transportadoraLabel) {
      setTransportadora({ id: String(selected.idTransp), label: selected.transportadoraLabel });
    } else {
      setTransportadora(null);
    }
  }, [selected]);

  const fetchClassificacoes = useCallback(
    async (q) => {
      const res = await fetch(
        `${baseUrl}/api/cadastros/tecnicos/gestao/classificacoes?q=${encodeURIComponent(q)}`,
        { headers: getAuthHeaders() },
      );
      const json = await res.json();
      if (!res.ok || !json.success) return [];
      return (json.data || []).map((r) => ({
        id: String(r.codigo),
        label: r.label || String(r.codigo),
      }));
    },
    [baseUrl],
  );

  const fetchTransportadoras = useCallback(
    async (q) => {
      const res = await fetch(
        `${baseUrl}/api/cadastros/tecnicos/gestao/transportadoras?q=${encodeURIComponent(q)}`,
        { headers: getAuthHeaders() },
      );
      const json = await res.json();
      if (!res.ok || !json.success) return [];
      return (json.data || []).map((r) => ({
        id: String(r.codigo),
        label: r.label || String(r.codigo),
      }));
    },
    [baseUrl],
  );

  const persistClassificacao = useCallback(
    async (item) => {
      if (!codigo) return;
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/classificacao`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, idClass: item ? item.id : null }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Não foi possível gravar a classificação');
      }
      setGestaoList(json.data || []);
    },
    [baseUrl, codigo],
  );

  const persistTransportadora = useCallback(
    async (item) => {
      if (!codigo) return;
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/transportadora`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, idTransp: item ? item.id : null }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Não foi possível gravar a transportadora');
      }
      setGestaoList(json.data || []);
    },
    [baseUrl, codigo],
  );

  const cargoDirty = useMemo(() => {
    if (!selected) return false;
    return draftCargo.trim() !== String(selected.cargo || '').trim();
  }, [draftCargo, selected]);

  const kitDirty = useMemo(() => {
    if (!selected) return false;
    const baseline = kitDraftFromSelected(selected);
    return TECH_KIT_FIELDS.some((f) => draftKit[f.api] !== baseline[f.api]);
  }, [draftKit, selected]);

  const classificacaoDirty = useMemo(() => {
    if (!selected?.gestaoHabilitado) return false;
    const base = selected.idClass && selected.classificacaoLabel ? String(selected.idClass) : '';
    return (classificacao?.id || '') !== base;
  }, [classificacao, selected]);

  const transportadoraDirty = useMemo(() => {
    if (!selected?.gestaoHabilitado) return false;
    const base = selected.idTransp && selected.transportadoraLabel ? String(selected.idTransp) : '';
    return (transportadora?.id || '') !== base;
  }, [transportadora, selected]);

  const formDirty = cargoDirty || kitDirty || classificacaoDirty || transportadoraDirty;

  const profileDirty = showProfile && JSON.stringify(profile) !== JSON.stringify(profileBase);

  useEffect(() => {
    if (!open || !showProfile) return undefined;
    let cancelled = false;
    const loadProfile = async () => {
      setProfileLoading(true);
      setSaveErr(null);
      setFieldErrors({});
      setCoordId('');
      try {
        const qs = !isCreate && codigo ? `?codigo=${encodeURIComponent(codigo)}` : '';
        const [usersRes, techRes] = await Promise.all([
          fetch(`${baseUrl}/api/team-native/users-available${qs}`, { headers: getAuthHeaders() }),
          isCreate
            ? null
            : fetch(`${baseUrl}/api/team-native/technicians/${encodeURIComponent(codigo)}`, { headers: getAuthHeaders() }),
        ]);
        const usersJson = await usersRes.json();
        if (cancelled) return;
        setProfileUsers(usersJson.success ? usersJson.data || [] : []);
        if (techRes) {
          const techJson = await techRes.json();
          if (!techRes.ok || !techJson.success) throw new Error(techJson.message || 'Falha ao carregar o técnico');
          const t = techJson.data;
          const next = {
            ...EMPTY_PROFILE,
            name: t.name || '',
            document: formatCpf(t.document),
            phone: formatPhone(t.phone),
            email: t.email || '',
            isTerritory: Boolean(t.isTerritory),
            userLegacyId: t.userLegacyId != null ? String(t.userLegacyId) : '',
            erpCode: t.erpCode || '',
            isActive: t.isActive !== false,
          };
          setProfileSource(t.source || 'native');
          setProfile(next);
          setProfileBase(next);
        } else {
          setProfileSource('native');
          setProfile(EMPTY_PROFILE);
          setProfileBase(EMPTY_PROFILE);
          setNewUserPassword('');
          setShowNewUserPassword(false);
        }
      } catch (e) {
        if (!cancelled) setSaveErr(e.message || 'Erro ao carregar dados');
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [open, showProfile, isCreate, codigo, baseUrl]);

  const setProfileField = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSaveErr(null);
  };

  const creatingUser = isCreate && profileSource === 'native' && profile.userLegacyId === CREATE_USER_VALUE;

  useEffect(() => {
    if (!creatingUser) {
      setPasswordFocused(false);
      return undefined;
    }
    let cancelled = false;
    fetchPassPolicy()
      .then((p) => {
        if (cancelled) return;
        setPassPolicy(p);
        setPasswordRules(validatePasswordForPolicy(newUserPassword, p).rules);
      })
      .catch(() => {
        if (!cancelled) setPassPolicy(null);
      });
    return () => { cancelled = true; };
  }, [creatingUser]);

  const saveProfile = async () => {
    const body = {
      name: profile.name.trim(),
      document: profile.document,
      phone: profile.phone.trim(),
      email: profile.email.trim(),
      isTerritory: profile.isTerritory,
    };
    if (profileSource === 'native') {
      let userLegacyId = profile.userLegacyId ? Number(profile.userLegacyId) : null;
      if (creatingUser) {
        const userRes = await fetch(`${baseUrl}/api/users`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user: body.email,
            username: body.name,
            password: newUserPassword,
            type: 'Técnico',
            coordinator: false,
            codtec: '',
            active: true,
          }),
        });
        const userJson = await userRes.json();
        if (!userRes.ok || !userJson.success) throw new Error(userJson.message || 'Não foi possível criar o usuário');
        userLegacyId = Number(userJson.userId || userJson.id);
        if (!(userLegacyId > 0)) throw new Error('Não foi possível criar o usuário');
      }
      body.userLegacyId = userLegacyId;
    }
    if (isCreate) {
      body.erpCode = profile.erpCode.trim();
      body.jobTitle = profile.jobTitle.trim();
      if (profile.areaId !== '') body.areaId = Number(profile.areaId);
    } else {
      body.isActive = profile.isActive;
    }
    const res = await fetch(
      isCreate
        ? `${baseUrl}/api/team-native/technicians`
        : `${baseUrl}/api/team-native/technicians/${encodeURIComponent(codigo)}`,
      { method: isCreate ? 'POST' : 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) },
    );
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível salvar');
    return body.name;
  };

  const saveKit = async () => {
    if (!codigo || !selected?.gestaoHabilitado || !formDirty) return;
    if (cargoDirty) {
      const trimmed = draftCargo.trim();
      if (trimmed.length === 1) throw new Error('Cargo: use 2+ caracteres ou deixe vazio.');
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/cargo`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, cargo: trimmed }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível salvar o cargo');
      setGestaoList(json.data || []);
    }
    if (kitDirty) {
      for (const f of TECH_KIT_FIELDS) {
        const v = String(draftKit[f.api] || '').trim();
        if (v && v.length !== f.len) {
          throw new Error(`${f.label}: informe exatamente ${f.len} caractere(s) ou deixe vazio.`);
        }
      }
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/kit-overrides`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, ...draftKit }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível salvar os parâmetros de kit');
      setGestaoList(json.data || []);
    }
    if (classificacaoDirty) await persistClassificacao(classificacao);
    if (transportadoraDirty) await persistTransportadora(transportadora);
  };

  const handleSave = async () => {
    if (saving) return;
    if (isCreate || (showProfile && profileDirty)) {
      const errors = profileFieldErrors(profile, { createUser: creatingUser });
      if (creatingUser) {
        if (!newUserPassword.trim()) errors.password = 'Senha é obrigatória';
        else if (passPolicy) {
          const pwdVal = validatePasswordForPolicy(newUserPassword, passPolicy);
          if (!pwdVal.valid) errors.password = pwdVal.errors[0] || 'Senha não atende aos requisitos';
        } else if (newUserPassword.length < 4) {
          errors.password = 'Senha deve ter no mínimo 4 caracteres';
        }
      }
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setSaveErr('Revise os campos destacados');
        setActiveTab('geral');
        return;
      }
    } else if (!profileDirty && !formDirty) {
      return;
    }
    setSaving(true);
    setSaveErr(null);
    try {
      let name = tech?.name;
      if (showProfile && (isCreate || profileDirty)) {
        name = await saveProfile();
      }
      if (!isCreate) await saveKit();
      onSaved?.(name);
      onClose?.();
    } catch (e) {
      setSaveErr(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !codigo) {
      setPhotoUrl('');
      setPhotoError('');
      return undefined;
    }
    let cancelled = false;
    const cod = String(codigo).trim();
    fetch(`${baseUrl}/api/users/profile-photos/by-codtec`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ codtecs: [cod] }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const urls = data?.urls && typeof data.urls === 'object' ? data.urls : {};
        setPhotoUrl(urls[cod] || '');
      })
      .catch(() => {
        if (!cancelled) setPhotoUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [open, codigo, baseUrl]);

  const uploadPhoto = async (file) => {
    if (!file || !codigo || photoBusyRef.current) return;
    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    const allowed = type === 'image/jpeg' || type === 'image/png' || type === 'image/webp'
      || /\.(jpe?g|png|webp)$/.test(name);
    if (!allowed) {
      setPhotoError('Formato não permitido. Use JPEG, PNG ou WebP.');
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }
    if (!file.size || file.size > 10 * 1024 * 1024) {
      setPhotoError('Imagem muito grande. Máximo 10MB.');
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }
    photoBusyRef.current = true;
    setPhotoBusy(true);
    setPhotoError('');
    let presignKey = '';
    let sent = false;
    try {
      const mimeType = type === 'image/png' || type === 'image/webp' || type === 'image/jpeg'
        ? type
        : (name.endsWith('.png') ? 'image/png' : name.endsWith('.webp') ? 'image/webp' : 'image/jpeg');
      const presignRes = await fetch(`${baseUrl}/api/users/profile-photo/for-codtec`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          codtec: String(codigo).trim(),
          mimeType,
          sizeBytes: file.size,
          fileName: file.name || 'avatar.jpg',
        }),
      });
      const presignData = await presignRes.json();
      if (!presignRes.ok || !presignData.success) {
        throw new Error(presignData.message || 'Falha ao preparar a foto');
      }
      presignKey = presignData.key;
      const uploadRes = await fetch(presignData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Falha ao enviar a imagem');
      sent = true;
      const confirmRes = await fetch(`${baseUrl}/api/users/profile-photo/for-codtec/confirm`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codtec: String(codigo).trim(), key: presignData.key }),
      });
      const confirmData = await confirmRes.json();
      if (!confirmRes.ok || !confirmData.success) {
        throw new Error(confirmData.message || 'Falha ao gravar a foto');
      }
      setPhotoUrl(confirmData.viewUrl || '');
    } catch (e) {
      if (presignKey && !sent) {
        fetch(`${baseUrl}/api/users/profile-photo/for-codtec/confirm`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ codtec: String(codigo).trim(), release: true }),
        }).catch(() => {});
      }
      setPhotoError(e.message || 'Erro ao enviar a foto');
    } finally {
      photoBusyRef.current = false;
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  if (!open || (!tech && !isCreate)) return null;

  const named = showProfile ? profile.name.trim() : (tech?.name || '');
  const displayName = named || (isCreate ? 'Novo técnico' : (tech?.name || 'Técnico'));
  const roleLabel = String(isCreate ? profile.jobTitle : draftCargo).trim() || 'Sem cargo definido';
  const areaLabel = isCreate
    ? (areas.find((a) => String(a.id) === String(profile.areaId))?.nome || '')
    : (tech?.areaName || '');
  const erpChip = showProfile ? profile.erpCode : '';
  let hue = 0;
  for (const ch of named || 'tecnico') hue = (hue * 31 + ch.charCodeAt(0)) >>> 0;
  hue = [252, 268, 210, 190, 160, 330, 24, 280][hue % 8];

  const kitFilledCount = TECH_KIT_FIELDS.reduce(
    (n, f) => n + (String(draftKit[f.api] || '').trim() ? 1 : 0),
    0,
  ) + (classificacao ? 1 : 0) + (transportadora ? 1 : 0);
  const kitTotal = TECH_KIT_FIELDS.length + 2;

  const tabs = [
    showProfile ? { id: 'geral', label: 'Geral', desc: 'Dados pessoais e vínculo', Icon: User } : null,
    !isCreate ? { id: 'kit', label: 'Kit técnico', desc: 'Regras de reposição do kit', Icon: Package } : null,
    !isCreate ? { id: 'especialidade', label: 'Especialidade', desc: 'Competências do técnico', Icon: Star } : null,
  ].filter(Boolean);
  const tab = tabs.some((t) => t.id === activeTab) ? activeTab : tabs[0].id;
  const currentTab = tabs.find((t) => t.id === tab);
  const dirty = (showProfile && profileDirty) || (!isCreate && formDirty);
  const showFooter = tab !== 'especialidade' && (isCreate || showProfile || selected?.gestaoHabilitado);

  const showKitField = (filled) => kitFilter === 'all' || (kitFilter === 'custom' ? filled : !filled);
  const pinRow = (f) => {
    const filled = String(draftKit[f.api] || '').trim().length > 0;
    if (!showKitField(filled)) return null;
    const RowIcon = f.Icon;
    return (
      <div key={f.api} className={`tm-row${filled ? ' is-custom' : ''}`}>
        <div className="tm-row-label">
          <span className="tm-row-icon" aria-hidden><RowIcon size={15} stroke={1.75} /></span>
          <div className="tm-row-text">
            <span className="tm-row-title">{f.label.replace(/^Status - /, '')}</span>
            {f.stockTag ? (
              <span className={`tm-pill is-${f.stockTone === 'warn' ? 'warning' : 'success'}`}>{f.stockTag}</span>
            ) : null}
          </div>
        </div>
        <div className="tm-row-control">
          <Eq2KitPinInput
            field={f}
            value={draftKit[f.api]}
            onChange={(next) => setDraftKit((prev) => ({ ...prev, [f.api]: next }))}
          />
          <TdpLabelWithHelp helpTooltip={f.help} />
        </div>
      </div>
    );
  };
  const identRows = KIT_IDENT_FIELDS.map(pinRow).filter(Boolean);
  const pendRows = KIT_PEND_FIELDS.map(pinRow).filter(Boolean);
  const statusRows = KIT_STATUS_FIELDS.map(pinRow).filter(Boolean);
  const showClass = showKitField(Boolean(classificacao));
  const showCarrier = showKitField(Boolean(transportadora));

  const modal = (
    <form
      className="tm-overlay tm-tech"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
      onSubmit={(e) => e.preventDefault()}
    >
      <div
        className={`tm-modal${tab === 'especialidade' ? ' is-spec' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tm-title"
      >
        <aside className="tm-sidebar">
          <div className="tm-profile">
            <button
              type="button"
              className={`tm-avatar${!named && !photoUrl ? ' is-empty' : ''}${photoBusy ? ' is-busy' : ''}`}
              style={{ '--tm-hue': hue }}
              aria-label={codigo ? 'Definir foto do técnico' : 'Salve o técnico para definir a foto'}
              title={codigo ? 'Definir foto' : 'Salve o técnico para definir a foto'}
              disabled={!codigo || photoBusy}
              onClick={() => photoInputRef.current?.click()}
            >
              {photoUrl ? (
                <img src={photoUrl} alt="" />
              ) : (
                named ? getInitials(named) : <UserPlus size={22} strokeWidth={1.75} />
              )}
              {codigo ? <span className="tm-avatar-edit">{photoBusy ? '…' : 'Foto'}</span> : null}
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(e) => uploadPhoto(e.target.files?.[0])}
            />
            <div className="tm-profile-text">
              {isCreate ? null : (
                <span className="tm-eyebrow">{showProfile ? 'Editar técnico' : 'Técnico'}</span>
              )}
              <h2 id="tm-title" className="tm-profile-name" title={displayName}>{displayName}</h2>
              <p className="tm-profile-role">{roleLabel}</p>
            </div>
            <div className="tm-profile-chips">
              {areaLabel ? (
                <span className="tm-chip"><MapPin size={12} strokeWidth={1.75} /> {areaLabel}</span>
              ) : null}
              {erpChip ? (
                <span className="tm-chip"><Hash size={12} strokeWidth={1.75} /> {erpChip}</span>
              ) : null}
            </div>
            {photoError ? <p className="tm-error">{photoError}</p> : null}
          </div>
          <nav className="tm-nav" role="tablist" aria-label="Seções do técnico">
            {tabs.map((t) => {
              const NavIcon = t.Icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`tm-nav-item${tab === t.id ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <NavIcon className="tm-icon" size={16} strokeWidth={1.75} />
                  <span className="tm-nav-label">{t.label}</span>
                  {t.id === 'kit' ? (
                    <span className={`tm-nav-badge${kitFilledCount ? ' is-brand' : ''}`}>{kitFilledCount}/{kitTotal}</span>
                  ) : null}
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="tm-main">
          <header className="tm-main-head">
            <div className="tm-main-heading">
              <h2 className="tm-main-title">{currentTab.label}</h2>
              <span className="tm-main-desc">{currentTab.desc}</span>
            </div>
            <button type="button" className="tm-icon-btn" onClick={onClose} aria-label="Fechar">
              <X size={18} strokeWidth={1.75} />
            </button>
          </header>

          <div className={`tm-body${tab === 'especialidade' ? ' is-spec' : ''}`}>
            {tab === 'geral' && showProfile ? (
              profileLoading ? (
                <p className="eq2-tdp-muted">Carregando…</p>
              ) : (
                <div className="tm-stack tm-fade">
                  <section className="tm-section">
                    <header className="tm-section-head">
                      <h3 className="tm-section-title">Dados pessoais</h3>
                      <p className="tm-section-desc">Informações de identificação e contato.</p>
                    </header>
                    <div className="tm-section-body">
                      <div className="tm-grid">
                        <TmField label="Nome completo" htmlFor="tm-nome" required className="tm-col-2" error={fieldErrors.name}>
                          <TmText id="tm-nome" icon={User} value={profile.name} maxLength={60} autoFocus={isCreate} className={fieldErrors.name ? 'is-invalid' : ''} onChange={(e) => setProfileField('name', e.target.value)} />
                        </TmField>
                        <TmField label="E-mail" htmlFor="tm-email" className="tm-col-2" required={creatingUser} error={fieldErrors.email}>
                          <TmText id="tm-email" icon={Mail} type="email" value={profile.email} maxLength={150} className={fieldErrors.email ? 'is-invalid' : ''} onChange={(e) => setProfileField('email', e.target.value)} />
                        </TmField>
                        <TmField label="CPF" htmlFor="tm-cpf" error={fieldErrors.document}>
                          <TmText id="tm-cpf" icon={Hash} inputMode="numeric" placeholder="000.000.000-00" value={profile.document} className={fieldErrors.document ? 'is-invalid' : ''} onChange={(e) => setProfileField('document', formatCpf(e.target.value))} />
                        </TmField>
                        <TmField label="Telefone" htmlFor="tm-tel" error={fieldErrors.phone}>
                          <TmText id="tm-tel" icon={Phone} inputMode="numeric" placeholder="(00) 00000-0000" value={profile.phone} maxLength={16} className={fieldErrors.phone ? 'is-invalid' : ''} onChange={(e) => setProfileField('phone', formatPhone(e.target.value))} />
                        </TmField>
                      </div>
                    </div>
                  </section>
                  <section className="tm-section">
                    <header className="tm-section-head">
                      <h3 className="tm-section-title">Vínculo</h3>
                      <p className="tm-section-desc">{isCreate ? 'Coordenador, área, cargo e usuário do sistema.' : 'Cargo e usuário do sistema.'}</p>
                    </header>
                    <div className="tm-section-body">
                      <div className="tm-grid">
                        {isCreate ? (
                          <>
                            <TmField label="Coordenador" htmlFor="tm-coord" hint={coordId ? 'Inclui as áreas deste coordenador e as que ainda não têm coordenador.' : undefined}>
                              <TmPicker
                                id="tm-coord"
                                value={coordId}
                                onChange={(v) => {
                                  setCoordId(v);
                                  setProfile((prev) => {
                                    if (!v || !prev.areaId) return prev;
                                    const area = areas.find((a) => String(a.id) === String(prev.areaId));
                                    if (!area || area.coordenadorId == null || String(area.coordenadorId) === String(v)) return prev;
                                    return { ...prev, areaId: '' };
                                  });
                                }}
                                placeholder="Todos"
                                emptyMessage="Nenhum coordenador"
                                ariaLabel="Coordenador"
                                options={[
                                  ...(selectedArea?.coordenadorId == null ? [{ value: '', label: 'Todos' }] : []),
                                  ...coordChoices.map((c) => ({ value: String(c.id), label: c.nome })),
                                ]}
                              />
                            </TmField>
                            <TmField label="Área" htmlFor="tm-area">
                              <TmPicker
                                id="tm-area"
                                value={profile.areaId}
                                onChange={(v) => {
                                  setProfileField('areaId', v);
                                  const area = areas.find((a) => String(a.id) === String(v));
                                  if (area?.coordenadorId != null) setCoordId(String(area.coordenadorId));
                                }}
                                placeholder="Sem área"
                                emptyMessage="Nenhuma área"
                                ariaLabel="Área"
                                options={[
                                  { value: '', label: 'Sem área' },
                                  ...areaChoices.map((a) => ({
                                    value: String(a.id),
                                    label: coordId && a.coordenadorId == null ? `${a.nome} (sem coordenador)` : a.nome,
                                  })),
                                ]}
                              />
                            </TmField>
                            <TmField label="Cargo" htmlFor="tm-cargo">
                              <TmText id="tm-cargo" icon={Briefcase} value={profile.jobTitle} maxLength={200} placeholder="Ex.: Técnico de campo" onChange={(e) => setProfileField('jobTitle', e.target.value)} />
                            </TmField>
                          </>
                        ) : null}
                        {profileSource === 'native' ? (
                          <TmField label="Usuário vinculado" htmlFor="tm-user" className="tm-col-2">
                            <TmPicker
                              id="tm-user"
                              value={profile.userLegacyId}
                              onChange={(v) => {
                                setProfileField('userLegacyId', v);
                                if (v !== CREATE_USER_VALUE) {
                                  setNewUserPassword('');
                                  setShowNewUserPassword(false);
                                }
                              }}
                              placeholder="Sem usuário"
                              emptyMessage="Nenhum usuário"
                              ariaLabel="Usuário vinculado"
                              options={[
                                { value: '', label: 'Sem usuário' },
                                ...(isCreate ? [{ value: CREATE_USER_VALUE, label: 'Criar novo usuário' }] : []),
                                ...profileUsers.map((u) => ({
                                  value: String(u.id),
                                  label: u.login && u.login !== u.nome ? `${u.nome} (${u.login})` : u.nome,
                                })),
                              ]}
                            />
                            {creatingUser ? (
                              <TmField label="Senha" htmlFor="tm-user-password" required error={fieldErrors.password} hint="Usuário App Go, sem código de técnico.">
                                <div className={`user-form-email-input user-form-password-input${fieldErrors.password ? ' user-form-password-input--error' : ''}`}>
                                  <KeyRound size={15} strokeWidth={2} className="user-form-email-input-icon" aria-hidden />
                                  <input
                                    id="tm-user-password"
                                    type={showNewUserPassword ? 'text' : 'password'}
                                    className="user-form-password-field"
                                    value={newUserPassword}
                                    placeholder="Defina uma senha"
                                    autoComplete="new-password"
                                    onFocus={() => setPasswordFocused(true)}
                                    onBlur={() => setPasswordFocused(false)}
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      setNewUserPassword(next);
                                      if (passPolicy) setPasswordRules(validatePasswordForPolicy(next, passPolicy).rules);
                                      setFieldErrors((prev) => {
                                        if (!prev.password) return prev;
                                        const n = { ...prev };
                                        delete n.password;
                                        return n;
                                      });
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="user-form-password-toggle"
                                    onClick={() => setShowNewUserPassword((v) => !v)}
                                    title={showNewUserPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                    aria-label={showNewUserPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                  >
                                    {showNewUserPassword ? <EyeOff size={15} strokeWidth={2} aria-hidden /> : <Eye size={15} strokeWidth={2} aria-hidden />}
                                  </button>
                                </div>
                                {passwordFocused && passwordRules.length > 0 ? (
                                  <ul className="password-policy-rules" aria-live="polite">
                                    {passwordRules.map((r) => (
                                      <li key={r.label} className={r.ok ? 'password-rule-ok' : 'password-rule-fail'}>
                                        <span className="password-rule-icon">{r.ok ? '✓' : '✗'}</span>
                                        <span>{r.label}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </TmField>
                            ) : null}
                          </TmField>
                        ) : null}
                        <TmField label="Código no ERP" htmlFor="tm-erp" hint={isCreate ? 'Opcional. Usado na integração com o ERP.' : undefined}>
                          <TmText
                            id="tm-erp"
                            icon={Hash}
                            value={profile.erpCode}
                            disabled={!isCreate}
                            placeholder={isCreate ? 'Ex.: 0312' : ''}
                            onChange={(e) => setProfileField('erpCode', e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 4))}
                          />
                        </TmField>
                      </div>
                      {!isCreate ? (
                        <label className={`tm-toggle-card${profile.isActive ? ' is-on' : ''}`} style={{ marginTop: 14 }}>
                          <span className="tm-toggle-icon" aria-hidden><User size={16} strokeWidth={1.75} /></span>
                          <span className="tm-toggle-text">
                            <span className="tm-toggle-title">Ativo</span>
                            <span className="tm-toggle-desc">Técnico inativo não entra na operação.</span>
                          </span>
                          <TmSwitch id="tm-ativo" checked={profile.isActive} onChange={(v) => setProfileField('isActive', v)} />
                        </label>
                      ) : null}
                    </div>
                  </section>
                </div>
              )
            ) : null}

            {tab === 'kit' ? (
              loading || enabling ? (
                <p className="eq2-tdp-muted">{enabling ? 'Habilitando técnico na gestão…' : 'Carregando dados do técnico…'}</p>
              ) : enableErr ? (
                <div className="tm-empty">
                  <p className="tm-empty-title">{enableErr}</p>
                  <button type="button" className="tm-btn is-primary" onClick={enableGestao}>Tentar novamente</button>
                </div>
              ) : !selected ? (
                <div className="tm-empty">
                  <p className="tm-empty-title">
                    {tech?.inactive
                      ? (showProfile ? 'Técnico inativo. Na aba Geral, marque Ativo para reativá-lo.' : 'Técnico inativo.')
                      : 'Técnico não encontrado na gestão.'}
                  </p>
                </div>
              ) : (
                <div className="tm-stack tm-fade">
                  <div className="tm-callout">
                    <Info size={16} strokeWidth={1.75} />
                    <p>Campos vazios usam a configuração global de Processos Go → Kit técnico.</p>
                  </div>
                  <div className="tm-toolbar">
                    <div className="tm-segment" role="radiogroup" aria-label="Filtrar campos">
                      {[
                        ['all', 'Todos', kitTotal],
                        ['custom', 'Personalizados', kitFilledCount],
                        ['inherit', 'Herdados', kitTotal - kitFilledCount],
                      ].map(([id, label, n]) => (
                        <button
                          key={id}
                          type="button"
                          role="radio"
                          aria-checked={kitFilter === id}
                          className={kitFilter === id ? 'is-active' : ''}
                          onClick={() => setKitFilter(id)}
                        >
                          {label} <span className="tm-segment-count">{n}</span>
                        </button>
                      ))}
                    </div>
                    <div className="tm-progress" title={`${kitFilledCount} de ${kitTotal} personalizados`}>
                      <div className="tm-progress-bar">
                        <span style={{ width: `${kitTotal ? (kitFilledCount / kitTotal) * 100 : 0}%` }} />
                      </div>
                      <span className="tm-progress-text">{kitFilledCount}/{kitTotal}</span>
                    </div>
                  </div>
                  <section className="tm-section">
                    <header className="tm-section-head">
                      <h3 className="tm-section-title">Cargo</h3>
                      <p className="tm-section-desc">Função exibida na equipe.</p>
                    </header>
                    <div className="tm-section-body">
                      <TmField label="Cargo" htmlFor="tm-kit-cargo">
                        <TmText id="tm-kit-cargo" icon={Briefcase} value={draftCargo} maxLength={200} placeholder="Ex.: Técnico de campo II" onChange={(e) => setDraftCargo(e.target.value)} />
                      </TmField>
                    </div>
                  </section>
                  {identRows.length ? (
                    <section className="tm-section">
                      <header className="tm-section-head">
                        <h3 className="tm-section-title">Identificação</h3>
                        <p className="tm-section-desc">Dados usados na geração do pedido de reposição do kit.</p>
                      </header>
                      <div className="tm-section-body"><div className="tm-rows">{identRows}</div></div>
                    </section>
                  ) : null}
                  {pendRows.length || statusRows.length ? (
                    <section className="tm-section">
                      <header className="tm-section-head">
                        <h3 className="tm-section-title">Status do kit</h3>
                        <p className="tm-section-desc">Status aplicado em cada etapa do fluxo do kit.</p>
                      </header>
                      <div className="tm-section-body"><div className="tm-rows">{pendRows}{statusRows}</div></div>
                    </section>
                  ) : null}
                  {showClass || showCarrier ? (
                    <section className="tm-section">
                      <header className="tm-section-head">
                        <h3 className="tm-section-title">Classificação e logística</h3>
                        <p className="tm-section-desc">Como o pedido é classificado e por quem é transportado.</p>
                      </header>
                      <div className="tm-section-body tm-kit-legacy">
                        {showClass ? (
                          <TdpSearchField icon={IconTags} label="Classificação" placeholder="Buscar classificação..." value={classificacao} onChange={setClassificacao} fetchSuggestions={fetchClassificacoes} mono />
                        ) : null}
                        {showCarrier ? (
                          <TdpSearchField icon={IconTruckDelivery} label="Transportadora" placeholder="Buscar transportadora..." value={transportadora} onChange={setTransportadora} fetchSuggestions={fetchTransportadoras} mono />
                        ) : null}
                      </div>
                    </section>
                  ) : null}
                  {!identRows.length && !pendRows.length && !statusRows.length && !showClass && !showCarrier ? (
                    <div className="tm-empty">
                      <p className="tm-empty-title">{kitFilter === 'custom' ? 'Nenhum campo personalizado' : 'Nenhum campo herdado'}</p>
                      <p className="tm-empty-desc">
                        {kitFilter === 'custom'
                          ? 'Este técnico usa a configuração global do kit.'
                          : 'Todos os campos deste técnico estão personalizados.'}
                      </p>
                    </div>
                  ) : null}
                </div>
              )
            ) : null}

            {tab === 'especialidade' ? (
              loading ? (
                <p className="eq2-tdp-muted">Carregando especialidades…</p>
              ) : selected ? (
                <div className="eq2-tdp-spec-pane">
                  <Equipe2TechDrawerSpecialty codigo={selected.codigo} baseUrl={baseUrl} />
                </div>
              ) : (
                <div className="tm-empty">
                  <p className="tm-empty-title">Especialidade disponível depois que o técnico entra na gestão.</p>
                </div>
              )
            ) : null}
          </div>

          {showFooter ? (
            <footer className="tm-footer">
              <span className={`tm-footer-status${saveErr ? ' is-danger' : ''}`}>
                {saveErr ? saveErr : (dirty ? (<><span className="tm-dot" /> Alterações não salvas</>) : null)}
              </span>
              <div className="tm-footer-actions">
                <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose} disabled={saving}>Cancelar</button>
                <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={handleSave} disabled={saving || profileLoading || (!isCreate && !dirty)}>
                  {saving ? 'Salvando…' : (isCreate ? 'Cadastrar' : 'Salvar')}
                </button>
              </div>
            </footer>
          ) : null}
        </div>
      </div>
    </form>
  );

  return ReactDOM.createPortal(modal, document.body);
}

export default Equipe2TechDetailDrawer;
